import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';

export type ToastTone = 'default' | 'success' | 'danger' | 'info';

export interface ToastOptions {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** Auto-dismiss after ms. 0 = sticky. Default 4000. */
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  /** Enqueue a toast; returns its id. */
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const toneIcon: Record<Exclude<ToastTone, 'default'>, IconName> = {
  success: 'check',
  danger: 'alert-circle',
  info: 'info',
};

export interface ToastProviderProps {
  children: ReactNode;
  position?: 'bottom-center' | 'top-right';
  /** Accessible name for the dismiss ✕. English default. */
  dismissLabel?: string;
}

/** Wrap the app once; children call useToast() to raise snackbars (brief §4.2). */
export function ToastProvider({
  children,
  position = 'bottom-center',
  dismissLabel = 'Dismiss',
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = Math.random().toString(36).slice(2);
      const duration = options.duration ?? 4000;
      setToasts((prev) => [...prev, { ...options, id }]);
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            className={cx('tc-toast-viewport', position === 'top-right' && 'tc-toast-viewport--top-right')}
            aria-live="polite"
            aria-atomic="false"
          >
            {toasts.map((t) => (
              <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} dismissLabel={dismissLabel} />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

function ToastCard({
  toast,
  onDismiss,
  dismissLabel,
}: {
  toast: ToastItem;
  onDismiss: () => void;
  dismissLabel: string;
}) {
  const tone = toast.tone ?? 'default';
  return (
    <div className="tc-toast" role={tone === 'danger' ? 'alert' : 'status'}>
      {tone !== 'default' && (
        <span className={cx('tc-toast__icon', `tc-toast__icon--${tone}`)} aria-hidden>
          <Icon name={toneIcon[tone]} size={14} strokeWidth={3} />
        </span>
      )}
      <div className="tc-toast__body">
        <span className="tc-toast__title">{toast.title}</span>
        {toast.description && <span className="tc-toast__desc">{toast.description}</span>}
      </div>
      {toast.action && (
        <button
          type="button"
          className="tc-toast__action"
          onClick={() => {
            toast.action!.onClick();
            onDismiss();
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button type="button" className="tc-toast__close" aria-label={dismissLabel} onClick={onDismiss}>
        ×
      </button>
    </div>
  );
}

/** Access the toast API. Must be inside a <ToastProvider>. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a <ToastProvider>.');
  return ctx;
}
